import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { RefreshCw, Calendar, ClipboardList, User, Building2, Plus, AlertTriangle, FileText, Eye, ChevronDown, Pencil, Ban, MoreVertical, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useMemo, useCallback, useEffect } from "react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { useReservations, useDeleteReservation } from "@/hooks/useReservations";
import { useCleaningTasks, useCreateSingleTask } from "@/hooks/useCleaningTasks";
import { useScheduleItems } from "@/hooks/useScheduleItems";
import { useChecklists } from "@/hooks/useChecklists";
import { usePropertyCleanerAssignments } from "@/hooks/useCleanerAssignments";
import { usePropertyCleaningAssignment, useCompanyCleanersForProperty, useAllCompanies } from "@/hooks/useCleaningCompanies";
import { usePropertyWithAccess } from "@/hooks/useProperties";
import { usePropertyAssignableMembers, USER_PREFIX } from "@/hooks/usePropertyAssignableMembers";
import { format } from "date-fns";
import { formatScheduledDate, parseLocalDateOnly } from "@/lib/dateUtils";
import type { CleaningTaskWithRelations, Reservation } from "@/types/cleaning";

/** Timeline filter for the unified schedule */
export type ScheduleFilter = "all" | "reservations" | "tasks" | "unassigned" | "needs_attention";

/** One entry in the unified timeline: either a reservation (with its tasks) or a standalone task */
type ReservationLike = Reservation & { source?: string | null; summary?: string | null; [key: string]: unknown };
export type ScheduleTimelineEntry =
  | { kind: "reservation"; sortDate: string; reservation: ReservationLike; tasks: CleaningTaskWithRelations[] }
  | { kind: "task"; sortDate: string; task: CleaningTaskWithRelations };
import { TaskDetailDrawer } from "./TaskDetailDrawer";
import { MessageThreadModal } from "@/components/messaging/MessageThreadModal";
import { TaskMessageButton } from "@/components/messaging/TaskMessageButton";
import { CreateTestFlowModal } from "./CreateTestFlowModal";
import { PastBookingsSection } from "./PastBookingsSection";
import { AddManualReservationModal } from "./AddManualReservationModal";
import { ScheduleCalendarView, type CalendarEmptyState } from "./ScheduleCalendarView";
import { ReservationDetailDrawer } from "./ReservationDetailDrawer";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { canOperateTasks } from "@/lib/propertyTabs";
import { useTurnoverTaskModal } from "@/contexts/TurnoverTaskModalContext";
import { useAuth } from "@/contexts/AuthContext";
import { usePropertyCanExecuteTasks } from "@/hooks/useMemberInvites";
import { MyTasksPanel } from "./MyTasksPanel";
import {
  displayNameFromProfile,
  formatPersonLabel,
  getTaskAssigneeBadgeLabel,
  getTaskAssigneeLabelForSchedule,
  isTaskAssignedForSchedule,
} from "@/lib/taskAssigneeLabel";
import { usePropertyTaskChecklistAssignments } from "@/hooks/usePropertyTaskChecklistAssignments";
import { SimpleScheduleJobs, type ScheduleJobsView } from "./SimpleScheduleJobs";
import { LiveJobDesk } from "./LiveJobDesk";
import { readLastSyncAt, writeLastSyncAt } from "@/lib/turnoverWindow";
import { useRefreshScheduleTurnoverWindows } from "@/hooks/useRefreshTurnoverWindow";
import {
  ScheduleMonthCalendar,
  turnoverSelectionKey,
} from "./ScheduleMonthCalendar";
import {
  ScheduleTurnoverDetail,
  type ScheduleAssignOption,
  type ScheduleTurnoverSelection,
} from "./ScheduleTurnoverDetail";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from "@/components/ui/drawer";

interface ScheduleTabProps {
  propertyId: string;
  hasIcalUrls: boolean;
  onConnectIcal?: () => void;
  /** False for team members with view-only schedule access */
  canAddTasks?: boolean;
  /** Deep link: open reservation drawer when schedule tab loads */
  focusReservationId?: string;
  /** Deep link: open task drawer when schedule tab loads */
  focusTaskId?: string;
  /** Slice 37 — jump to Unit tab from live desk empty state */
  onGoToUnit?: () => void;
}

const SCHEDULE_VIEW_KEY = "schedule_view_v43";

function getStoredScheduleView(propertyId: string): ScheduleJobsView {
  try {
    const v = localStorage.getItem(`${SCHEDULE_VIEW_KEY}:${propertyId}`);
    if (v === "list" || v === "month") return v;
    // Migrate legacy timeline/calendar keys
    const legacy = localStorage.getItem(`schedule_view:${propertyId}`);
    if (legacy === "calendar") return "month";
  } catch {}
  return "list";
}

function setStoredScheduleView(propertyId: string, view: ScheduleJobsView) {
  try {
    localStorage.setItem(`${SCHEDULE_VIEW_KEY}:${propertyId}`, view);
  } catch {}
}

const ScheduleTab = ({ propertyId, hasIcalUrls, onConnectIcal, canAddTasks = true, focusReservationId, focusTaskId, onGoToUnit }: ScheduleTabProps) => {
  const ctx = usePropertyContext();
  const isOwner = ctx?.isOwner ?? true;
  const role = ctx?.role ?? "owner";
  const { canExecuteTasks: canExecuteTasksForTeam } = usePropertyCanExecuteTasks(propertyId, isOwner);
  const { data: property } = usePropertyWithAccess(propertyId);
  const { data: reservations, isLoading } = useReservations(propertyId, { live: true });
  const { data: cleaningTasks, isLoading: tasksLoading } = useCleaningTasks(propertyId);
  const { items: scheduleItems } = useScheduleItems(propertyId);
  const { data: checklists } = useChecklists(propertyId);
  const displayChecklists = (checklists && Array.isArray(checklists)) ? checklists : [];
  const hid = String((property as { hospitable_property_id?: string | null } | null)?.hospitable_property_id || "").trim();
  const displayReservations = useMemo(() => {
    const all = Array.isArray(reservations) ? reservations : [];
    return all.filter((r) => {
      const row = r as { is_test?: boolean; external_id?: string | null };
      // Keep dogfood Test cleans visible on Schedule.
      if (row.is_test) return true;
      if (!hid) return true;
      return String(row.external_id || "").startsWith("hospitable:");
    });
  }, [reservations, hid]);
  const displayTasks = useMemo(() => {
    const all = Array.isArray(cleaningTasks) ? cleaningTasks : [];
    const resById = new Map(
      displayReservations.map((r) => [r.id, r as { external_id?: string | null; is_test?: boolean }]),
    );
    return all.filter((t) => {
      const row = t as { is_test?: boolean; reservation_id?: string | null };
      // Always show Test clean jobs so they can be assigned from Schedule.
      if (row.is_test) return true;
      if (!hid) return true;
      if (!row.reservation_id) return true;
      const res = resById.get(row.reservation_id);
      if (!res) return false;
      if (res.is_test) return true;
      return String(res.external_id || "").startsWith("hospitable:");
    });
  }, [cleaningTasks, displayReservations, hid]);
  const taskIds = useMemo(() => displayTasks.map((t) => t.id), [displayTasks]);
  const { data: checklistAssignmentsByTaskId } = usePropertyTaskChecklistAssignments(taskIds);
  const { data: cleanerAssignments } = usePropertyCleanerAssignments(propertyId);
  const { data: companyAssignment } = usePropertyCleaningAssignment(propertyId);
  const { data: companyCleaners } = useCompanyCleanersForProperty(companyAssignment?.company_id);
  const { data: allCompanies } = useAllCompanies();
  const { user, profile } = useAuth();
  const { members: assignableMembers } = usePropertyAssignableMembers(propertyId);
  const canAssignTasks =
    !!ctx?.isOwner ||
    canOperateTasks(ctx?.role ?? "cohost", ctx?.accessLevel ?? null, ctx?.permissions ?? null);
  const createSingleTask = useCreateSingleTask();

  // Map assignee_user_id → display name (owner + team); robust fallback so no blank label
  const assigneeUserIdToName = useMemo(() => {
    const m = new Map<string, string>();
    const ownerId = (property as { owner_id?: string } | null)?.owner_id;
    if (ownerId && user?.id === ownerId) {
      m.set(ownerId, displayNameFromProfile(profile as { full_name?: string | null; email?: string | null } | null, "Owner"));
    }
    for (const member of assignableMembers ?? []) {
      if (!member?.value) continue;
      if (member.value.startsWith(USER_PREFIX) && member.userId) {
        m.set(member.userId, (member.name?.trim() || member.email?.split("@")[0] || "Team"));
      }
    }
    return m;
  }, [assignableMembers, property, user?.id, profile]);
  const { openTurnoverTaskModal } = useTurnoverTaskModal();
  const [syncing, setSyncing] = useState(false);
  const [syncFailed, setSyncFailed] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<number | null>(() => readLastSyncAt(propertyId));
  useEffect(() => {
    setLastSyncAt(readLastSyncAt(propertyId));
    setSyncFailed(false);
  }, [propertyId]);

  // Quiet auto-sync every 12h when calendar is linked (catch check-in/out changes).
  useEffect(() => {
    if (!hasIcalUrls || !propertyId) return;
    const AUTO_SYNC_MS = 12 * 60 * 60 * 1000;
    const last = readLastSyncAt(propertyId);
    if (last && Date.now() - last < AUTO_SYNC_MS) return;
    let cancelled = false;
    (async () => {
      setSyncing(true);
      try {
        const hid = String((property as { hospitable_property_id?: string | null } | null)?.hospitable_property_id || "").trim();
        if (hid) {
          const { error } = await supabase.functions.invoke("hospitable-proxy", {
            body: { action: "sync_from_hospitable", property_id: propertyId, hospitable_property_id: hid },
          });
          if (error) throw error;
          queryClient.invalidateQueries({ queryKey: ["property-with-access", propertyId] });
        } else {
          const { error } = await supabase.functions.invoke("sync-ical", { body: { propertyId } });
          if (error) throw error;
        }
        if (cancelled) return;
        const at = Date.now();
        writeLastSyncAt(propertyId, at);
        setLastSyncAt(at);
        queryClient.invalidateQueries({ queryKey: ["reservations", propertyId] });
        queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", propertyId] });
      } catch {
        if (!cancelled) setSyncFailed(true);
      } finally {
        if (!cancelled) setSyncing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // intentionally once per property mount / ical flag
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, hasIcalUrls]);
  const [taskDetailDrawerOpen, setTaskDetailDrawerOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<CleaningTaskWithRelations | null>(null);
  const [liveJobTask, setLiveJobTask] = useState<CleaningTaskWithRelations | null>(null);
  const [expandedReservations, setExpandedReservations] = useState<Set<string>>(new Set());
  const [messagesTaskId, setMessagesTaskId] = useState<string | null>(null);
  const [testFlowModalOpen, setTestFlowModalOpen] = useState(false);
  const [manualReservationModalOpen, setManualReservationModalOpen] = useState(false);
  const [showBlocked, setShowBlocked] = useState(false);
  const [scheduleFilter, setScheduleFilter] = useState<ScheduleFilter>("all");
  const [timelineAssigneeFilter, setTimelineAssigneeFilter] = useState<string>("all");
  const [timelineChecklistFilter, setTimelineChecklistFilter] = useState<string>("all");
  const [scheduleView, setScheduleView] = useState<ScheduleJobsView>(() => getStoredScheduleView(propertyId));
  const [calendarDate, setCalendarDate] = useState(() => new Date());
  const [turnoverSelection, setTurnoverSelection] = useState<ScheduleTurnoverSelection | null>(null);
  const isMobile = useIsMobile();
  const [reservationDrawerOpen, setReservationDrawerOpen] = useState(false);
  const [selectedReservationForDrawer, setSelectedReservationForDrawer] = useState<Record<string, unknown> | null>(null);
  const [reservationToRemove, setReservationToRemove] = useState<{ id: string; hasAssignedTask: boolean } | null>(null);
  const queryClient = useQueryClient();
  const deleteReservation = useDeleteReservation(propertyId);

  // Keep List + Month in sync when Hospitable poll flips guest-left / checkout times.
  useEffect(() => {
    if (!propertyId) return;
    const channel = supabase
      .channel(`schedule-reservations-${propertyId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "reservations",
          filter: `property_id=eq.${propertyId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["reservations", propertyId] });
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [propertyId, queryClient]);

  useRefreshScheduleTurnoverWindows(propertyId, displayTasks, Boolean(hid));

  const calendarEmptyState = useMemo((): CalendarEmptyState => {
    if (!canAddTasks) return "view_only";
    if (!hasIcalUrls && scheduleItems.length === 0) return "no_schedule";
    if (!hasIcalUrls && scheduleItems.length > 0) return "manual_schedule";
    return "none";
  }, [canAddTasks, hasIcalUrls, scheduleItems.length]);

  const setScheduleViewPersisted = useCallback(
    (view: ScheduleJobsView) => {
      setScheduleView(view);
      setStoredScheduleView(propertyId, view);
      if (view === "list") setTurnoverSelection(null);
    },
    [propertyId]
  );

  useEffect(() => {
    setScheduleView(getStoredScheduleView(propertyId));
  }, [propertyId]);

  useEffect(() => {
    if (isLoading || tasksLoading) return;
    if (focusReservationId && Array.isArray(reservations)) {
      const r = reservations.find((x: { id?: string }) => x.id === focusReservationId);
      if (r) {
        setSelectedReservationForDrawer(r as Record<string, unknown>);
        setReservationDrawerOpen(true);
      }
    }
    if (focusTaskId && Array.isArray(cleaningTasks)) {
      const t = cleaningTasks.find((x: { id?: string }) => x.id === focusTaskId);
      if (t) {
        setSelectedTask(t as CleaningTaskWithRelations);
        setTaskDetailDrawerOpen(true);
      }
    }
  }, [focusReservationId, focusTaskId, reservations, cleaningTasks, isLoading, tasksLoading]);

  // Part 4: availableCleaners = direct cleaner_assignments + company cleaners when property has company
  // Labels use "First L." so duplicate first names stay distinguishable in assign UI.
  const assignedCleaners = ((cleanerAssignments?.map(a => a.cleaner).filter(Boolean) || []) as Array<{ id: string; name: string; user_id?: string | null }>).map(
    (c) => ({ ...c, name: formatPersonLabel(c.name) || c.name }),
  );
  const availableCleaners: Array<{ id: string; name: string; user_id?: string | null }> = (companyCleaners
    ? [...assignedCleaners, ...((companyCleaners as Array<{ id: string; name: string; user_id?: string | null }>).filter(cc => !assignedCleaners.some(ac => ac.id === cc.id)) || [])]
    : assignedCleaners
  ).map((c) => ({ ...c, name: formatPersonLabel(c.name) || c.name }));

  // Get the company name
  const assignedCompany = allCompanies?.find(c => c.id === companyAssignment?.company_id);

  const handleSync = async () => {
    setSyncing(true);
    setSyncFailed(false);
    try {
      const hid = String((property as { hospitable_property_id?: string | null } | null)?.hospitable_property_id || "").trim();
      let photoCount = 0;
      let warning: string | undefined;
      // Hospitable-linked units: ONE source of truth. Do not also run sync-ical —
      // iCal uses different external_ids and duplicates turnovers.
      if (hid) {
        const { data, error } = await supabase.functions.invoke("hospitable-proxy", {
          body: { action: "sync_from_hospitable", property_id: propertyId, hospitable_property_id: hid },
        });
        // Prefer JSON body error (edge returns { error: "..." } on 4xx)
        let bodyErr = data && typeof data === "object" ? String((data as { error?: string }).error || "") : "";
        if (!bodyErr && error && typeof error === "object" && "context" in error) {
          try {
            const ctx = (error as { context?: Response }).context;
            if (ctx && typeof ctx.json === "function") {
              const body = await ctx.json();
              bodyErr = String(body?.error || "");
            }
          } catch {
            /* ignore */
          }
        }
        if (bodyErr) throw new Error(bodyErr);
        if (error) throw error;
        photoCount = Number((data as { photos?: number } | null)?.photos) || 0;
        const warns = (data as { warnings?: string[] } | null)?.warnings;
        if (Array.isArray(warns) && warns[0]) warning = warns[0];
        queryClient.invalidateQueries({ queryKey: ["property", propertyId] });
        queryClient.invalidateQueries({ queryKey: ["property-with-access", propertyId] });
        queryClient.invalidateQueries({ queryKey: ["overview-next-clean", propertyId] });
      } else if (hasIcalUrls) {
        const { error } = await supabase.functions.invoke("sync-ical", {
          body: { propertyId },
        });
        if (error) throw error;
      } else {
        onConnectIcal?.();
        return;
      }

      const at = Date.now();
      writeLastSyncAt(propertyId, at);
      setLastSyncAt(at);
      toast({
        title: "Calendar updated",
        description: photoCount > 0
          ? `${photoCount} listing photos synced${warning ? ` · ${warning}` : ""}`
          : warning,
      });
      queryClient.invalidateQueries({ queryKey: ["reservations", propertyId] });
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", propertyId] });
    } catch (error) {
      console.error("Error syncing calendar:", error);
      setSyncFailed(true);
      const raw =
        error instanceof Error && error.message && !/non-2xx/i.test(error.message)
          ? error.message
          : undefined;
      const detail = raw && /unauthenticated/i.test(raw)
        ? "Hospitable rejected the saved token. Open Settings → Hospitable, paste a new personal access token, then sync again."
        : raw;
      toast({
        title: "Couldn’t sync",
        description: detail,
        variant: "destructive",
      });
    } finally {
      setSyncing(false);
    }
  };

  // Helper to get cleaner name by id (fallback so no blank)
  const getCleanerName = useCallback((cleanerId: string | null) => {
    if (!cleanerId) return null;
    return availableCleaners.find(c => c.id === cleanerId)?.name?.trim() || null;
  }, [availableCleaners]);

  const getChecklistRowsForTask = useCallback(
    (task: CleaningTaskWithRelations) => checklistAssignmentsByTaskId?.get(task.id),
    [checklistAssignmentsByTaskId],
  );

  const getAssigneeLabel = useCallback((task: CleaningTaskWithRelations) => {
    const maps = {
      getCleanerName,
      getAssigneeUserName: (userId: string | null) => (userId ? assigneeUserIdToName.get(userId) ?? null : null),
    };
    return getTaskAssigneeLabelForSchedule(
      task as { assigned_type?: string; cleaner_id?: string | null; assignee_user_id?: string | null; coordinator_user_id?: string | null },
      getChecklistRowsForTask(task),
      maps,
    );
  }, [getCleanerName, assigneeUserIdToName, getChecklistRowsForTask]);

  const isAssigned = useCallback(
    (task: CleaningTaskWithRelations) =>
      isTaskAssignedForSchedule(
        task as { assigned_type?: string; cleaner_id?: string | null; assignee_user_id?: string | null },
        getChecklistRowsForTask(task),
      ),
    [getChecklistRowsForTask],
  );

  const getAssigneeBadge = useCallback(
    (task: CleaningTaskWithRelations) => getTaskAssigneeBadgeLabel(getAssigneeLabel(task)),
    [getAssigneeLabel],
  );

  // Unique assignee labels and checklist names for timeline filters (from all tasks on property)
  const timelineAssigneeOptions = useMemo(() => {
    const labels = new Set<string>();
    labels.add("Unassigned");
    displayTasks.forEach((t) => {
      const label = isAssigned(t) ? getAssigneeLabel(t) : "Unassigned";
      labels.add(label);
    });
    return Array.from(labels).sort((a, b) => (a === "Unassigned" ? -1 : a.localeCompare(b)));
  }, [displayTasks, getAssigneeLabel, isAssigned]);

  const timelineChecklistOptions = useMemo(() => {
    const names = new Set<string>();
    displayTasks.forEach((t) => {
      const name = (t.checklists as { name?: string } | null)?.name?.trim() || "No checklist";
      names.add(name);
    });
    return Array.from(names).sort((a, b) => (a === "No checklist" ? 1 : a.localeCompare(b)));
  }, [displayTasks]);

  const showChecklistFilter = timelineChecklistOptions.length > 1;

  // Map reservation IDs to their cleaning tasks (array to support multiple tasks)
  const reservationToTasks = useMemo(() => {
    const map = new Map<string, CleaningTaskWithRelations[]>();
    displayTasks.forEach(task => {
      if (task.reservation_id) {
        const existing = map.get(task.reservation_id) || [];
        existing.push(task);
        map.set(task.reservation_id, existing);
      }
    });
    return map;
  }, [displayTasks]);

  // Helper to check if reservation is blocked
  const isBlockedReservation = (r: any) => {
    return r.reservation_type === "blocked" || 
           (r.summary && r.summary.toLowerCase().includes("not available"));
  };

  // Sort reservations by checkout date (end_date) ascending
  const sortedReservations = useMemo(() => {
    const filtered = showBlocked 
      ? displayReservations 
      : displayReservations.filter(r => !isBlockedReservation(r));
    return [...filtered].sort((a, b) => 
      new Date(a.end_date).getTime() - new Date(b.end_date).getTime()
    );
  }, [displayReservations, showBlocked]);

  // Count blocked reservations
  const blockedCount = useMemo(() => {
    return displayReservations.filter(r => isBlockedReservation(r)).length;
  }, [displayReservations]);

  // Standalone tasks (no reservation) grouped by scheduled_date (kept for any legacy use)
  const standaloneTasksByDate = useMemo(() => {
    const standalones = displayTasks.filter((t) => !t.reservation_id);
    const byDate = new Map<string, CleaningTaskWithRelations[]>();
    for (const t of standalones) {
      const d = t.scheduled_date;
      if (!byDate.has(d)) byDate.set(d, []);
      byDate.get(d)!.push(t);
    }
    return Array.from(byDate.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [displayTasks]);

  const today = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
  const isOverdue = useCallback((task: CleaningTaskWithRelations) => {
    if (task.status === "completed") return false;
    return task.scheduled_date < today;
  }, [today]);

  const taskNeedsAttention = useCallback((task: CleaningTaskWithRelations) => {
    const unassigned = !isAssigned(task);
    return !task.checklist_id || unassigned || isOverdue(task);
  }, [isOverdue, isAssigned]);

  // Unified timeline: merge reservations and standalone tasks into one sorted list
  const allTimelineEntries = useMemo((): ScheduleTimelineEntry[] => {
    const entries: ScheduleTimelineEntry[] = [];
    sortedReservations.forEach((reservation) => {
      const tasks = reservationToTasks.get(reservation.id) || [];
      entries.push({
        kind: "reservation",
        sortDate: reservation.end_date,
        reservation: reservation as ReservationLike,
        tasks,
      });
    });
    displayTasks.filter((t) => !t.reservation_id).forEach((task) => {
      entries.push({ kind: "task", sortDate: task.scheduled_date, task });
    });
    entries.sort((a, b) => a.sortDate.localeCompare(b.sortDate));
    return entries;
  }, [sortedReservations, reservationToTasks, displayTasks]);

  const entryMatchesFilter = useCallback((entry: ScheduleTimelineEntry): boolean => {
    if (scheduleFilter === "all") return true;
    if (scheduleFilter === "reservations") return entry.kind === "reservation";
    if (scheduleFilter === "tasks") {
      if (entry.kind === "task") return true;
      return entry.kind === "reservation" && entry.tasks.length > 0;
    }
    if (scheduleFilter === "unassigned") {
      if (entry.kind === "task") return !isAssigned(entry.task);
      // Reservations with no tasks (turnover needed) or with any unassigned task
      return entry.tasks.length === 0 || entry.tasks.some((t) => !isAssigned(t));
    }
    if (scheduleFilter === "needs_attention") {
      if (entry.kind === "reservation") {
        if (entry.tasks.length === 0) return true;
        return entry.tasks.some(taskNeedsAttention);
      }
      return taskNeedsAttention(entry.task);
    }
    return true;
  }, [scheduleFilter, taskNeedsAttention, isAssigned]);

  const filteredTimelineEntries = useMemo(() => allTimelineEntries.filter(entryMatchesFilter), [allTimelineEntries, entryMatchesFilter]);

  // Timeline-only: filter by assignee and/or checklist name
  const timelineFilteredEntries = useMemo(() => {
    if (timelineAssigneeFilter === "all" && timelineChecklistFilter === "all") return filteredTimelineEntries;
    const getChecklistName = (t: CleaningTaskWithRelations) =>
      (t.checklists as { name?: string } | null)?.name?.trim() || "No checklist";
    const matchesEntry = (entry: ScheduleTimelineEntry): boolean => {
      const tasks = entry.kind === "task" ? [entry.task] : entry.tasks;
      const matchAssignee =
        timelineAssigneeFilter === "all" ||
        tasks.some((t) =>
          timelineAssigneeFilter === "Unassigned" ? !isAssigned(t) : getAssigneeLabel(t) === timelineAssigneeFilter
        );
      const matchChecklist =
        timelineChecklistFilter === "all" ||
        tasks.some((t) => getChecklistName(t) === timelineChecklistFilter);
      return matchAssignee && matchChecklist;
    };
    return filteredTimelineEntries.filter(matchesEntry);
  }, [
    filteredTimelineEntries,
    timelineAssigneeFilter,
    timelineChecklistFilter,
    getAssigneeLabel,
    isAssigned,
  ]);

  // Calendar items: when Unassigned filter is on, show only unassigned tasks, turnover_needed, and reservations with no task or an unassigned task
  const calendarItems = useMemo(() => {
    if (scheduleFilter !== "unassigned") return scheduleItems;
    const reservationIdsWithTask = new Set(displayTasks.map((t) => t.reservation_id).filter(Boolean));
    const reservationIdsWithUnassignedTask = new Set(
      displayTasks.filter((t) => t.reservation_id && !isAssigned(t)).map((t) => t.reservation_id!),
    );
    return scheduleItems.filter((item) => {
      if (item.type === "task") return item.isAssigned === false;
      if (item.type === "turnover_needed") return true;
      if (item.type === "reservation") {
        const resId = (item.reservationPayload as { id?: string })?.id ?? String(item.id).replace(/^res-/, "");
        return !reservationIdsWithTask.has(resId) || reservationIdsWithUnassignedTask.has(resId);
      }
      return false;
    });
  }, [scheduleFilter, scheduleItems, displayTasks, isAssigned]);

  // Group filtered entries by date for rendering (uses timeline assignee/checklist filter in timeline view)
  const timelineEntriesByDate = useMemo(() => {
    const byDate = new Map<string, ScheduleTimelineEntry[]>();
    timelineFilteredEntries.forEach((entry) => {
      const d = entry.sortDate;
      if (!byDate.has(d)) byDate.set(d, []);
      byDate.get(d)!.push(entry);
    });
    return Array.from(byDate.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [timelineFilteredEntries]);

  // Toggle expand/collapse for a reservation
  const toggleExpanded = (reservationId: string) => {
    setExpandedReservations(prev => {
      const next = new Set(prev);
      if (next.has(reservationId)) {
        next.delete(reservationId);
      } else {
        next.add(reservationId);
      }
      return next;
    });
  };

  const openCreateTurnoverModal = useCallback(
    (reservation: Reservation) => {
      openTurnoverTaskModal({
        mode: "create",
        propertyId,
        reservationId: reservation.id,
      });
    },
    [openTurnoverTaskModal, propertyId]
  );

  const handleQuickCreateTask = useCallback(
    (reservation: Reservation) => {
      openCreateTurnoverModal(reservation);
    },
    [openCreateTurnoverModal]
  );

  const isCreating = createSingleTask.isPending;

  // Open task detail drawer
  const openTaskDrawer = (task: CleaningTaskWithRelations) => {
    setSelectedTask(task);
    setTaskDetailDrawerOpen(true);
  };

  const openEditModal = useCallback(
    (task: CleaningTaskWithRelations) => {
      openTurnoverTaskModal({ mode: "edit", propertyId, taskId: task.id });
    },
    [openTurnoverTaskModal, propertyId]
  );

  const getSourceBadgeColor = (source: string | null) => {
    switch (source?.toLowerCase()) {
      case "airbnb":
        return "bg-red-100 text-red-800";
      case "vrbo":
        return "bg-blue-100 text-blue-800";
      case "booking":
        return "bg-indigo-100 text-indigo-800";
      case "test":
        return "bg-amber-100 text-amber-800 border border-amber-300";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "in_progress":
        return "bg-blue-100 text-blue-800";
      case "missed":
        return "bg-red-100 text-red-800";
      case "scheduled":
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "completed":
        return "Completed";
      case "in_progress":
        return "In Progress";
      case "missed":
        return "Missed";
      case "scheduled":
      default:
        return "Scheduled";
    }
  };


  const noScheduleConnected = !hasIcalUrls && sortedReservations.length === 0;

  const monthReservations = useMemo(
    () =>
      displayReservations.map((r) => ({
        id: r.id,
        start_date: r.start_date,
        end_date: r.end_date,
        guest_name: (r as { guest_name?: string | null }).guest_name ?? null,
        guest_count: (r as { guest_count?: number | null }).guest_count ?? null,
        guest_photo_url: (r as { guest_photo_url?: string | null }).guest_photo_url ?? null,
        check_in_time: (r as { check_in_time?: string | null }).check_in_time ?? null,
        check_out_time: (r as { check_out_time?: string | null }).check_out_time ?? null,
        check_out_previous: (r as { check_out_previous?: string | null }).check_out_previous ?? null,
        check_out_slipped_at: (r as { check_out_slipped_at?: string | null }).check_out_slipped_at ?? null,
        early_checkout_detected: (r as { early_checkout_detected?: boolean | null }).early_checkout_detected ?? null,
        checkout_unconfirmed_at: (r as { checkout_unconfirmed_at?: string | null }).checkout_unconfirmed_at ?? null,
        reservation_type: (r as { reservation_type?: string | null }).reservation_type ?? null,
        is_test: !!(r as { is_test?: boolean }).is_test,
        summary: (r as { summary?: string | null }).summary ?? null,
      })),
    [displayReservations],
  );

  const scheduleAssignOptions = useMemo((): ScheduleAssignOption[] => {
    const opts: ScheduleAssignOption[] = [];
    const seenUserIds = new Set<string>();
    const seenCleanerIds = new Set<string>();
    const seenNames: string[] = [];

    const nameOverlaps = (a: string, b: string) => {
      const na = a.trim().toLowerCase().replace(/\./g, "");
      const nb = b.trim().toLowerCase().replace(/\./g, "");
      if (!na || !nb) return false;
      if (na === nb) return true;
      const pa = na.split(/\s+/).filter(Boolean);
      const pb = nb.split(/\s+/).filter(Boolean);
      if (pa[0] !== pb[0]) return false;
      const la = pa[1] || "";
      const lb = pb[1] || "";
      if (!la || !lb) return true; // same first name only
      if (la.length === 1 && lb.startsWith(la)) return true;
      if (lb.length === 1 && la.startsWith(lb)) return true;
      return la === lb;
    };

    // Prefer assignableMembers (already dedupes cleaner↔team by userId, includes avatars)
    for (const m of assignableMembers ?? []) {
      if (!m?.value) continue;
      if (m.userId) seenUserIds.add(m.userId);
      if (m.cleanerId) seenCleanerIds.add(m.cleanerId);
      const name = formatPersonLabel(m.name) || m.name;
      seenNames.push(name);
      opts.push({
        value: m.value,
        name,
        role: m.role === "Cleaner" ? "Cleaner" : m.role === "Team admin" ? "Team" : m.role,
        avatarUrl: m.avatarUrl ?? null,
      });
    }

    // Legacy cleaners not already represented via assignableMembers
    for (const c of availableCleaners) {
      if (seenCleanerIds.has(c.id)) continue;
      const row = c as { id: string; name: string; user_id?: string | null };
      if (row.user_id && seenUserIds.has(row.user_id)) continue;
      const name = formatPersonLabel(c.name) || c.name;
      if (seenNames.some((n) => nameOverlaps(n, name))) continue;
      opts.push({
        value: c.id,
        name,
        role: "Cleaner",
        avatarUrl: null,
      });
      seenCleanerIds.add(c.id);
      seenNames.push(name);
      if (row.user_id) seenUserIds.add(row.user_id);
    }

    opts.push({ value: "unassigned", name: "Unassigned" });

    // Owner self only if they are not already in the list as a team/cleaner row
    if (isOwner && user?.id && !seenUserIds.has(user.id)) {
      const selfName =
        (profile as { full_name?: string | null } | null)?.full_name?.trim() || "me";
      if (!seenNames.some((n) => nameOverlaps(n, selfName))) {
        opts.push({
          value: "self",
          name: "Assign to me",
          role: "Owner",
          avatarUrl: (profile as { avatar_url?: string | null } | null)?.avatar_url ?? null,
        });
      }
    }
    return opts;
  }, [assignableMembers, availableCleaners, isOwner, user?.id, profile]);

  const getAssigneeAvatarUrl = useCallback(
    (task: CleaningTaskWithRelations) => {
      const t = task as {
        assigned_type?: string | null;
        cleaner_id?: string | null;
        assignee_user_id?: string | null;
      };
      if (t.assignee_user_id) {
        const byUser = scheduleAssignOptions.find(
          (o) => o.value === `${USER_PREFIX}${t.assignee_user_id}` || (o.value === "self" && user?.id === t.assignee_user_id),
        );
        if (byUser?.avatarUrl) return byUser.avatarUrl;
      }
      if (t.cleaner_id) {
        const byCleaner = scheduleAssignOptions.find((o) => o.value === t.cleaner_id);
        if (byCleaner?.avatarUrl) return byCleaner.avatarUrl;
      }
      if (t.assigned_type === "owner") {
        const self = scheduleAssignOptions.find((o) => o.value === "self");
        if (self?.avatarUrl) return self.avatarUrl;
        return (profile as { avatar_url?: string | null } | null)?.avatar_url ?? null;
      }
      return null;
    },
    [scheduleAssignOptions, user?.id, profile],
  );

  // Keep month panel task row fresh after assign/sync/create
  useEffect(() => {
    if (!turnoverSelection) return;
    if (turnoverSelection.task) {
      const fresh = displayTasks.find((t) => t.id === turnoverSelection.task!.id);
      if (fresh && fresh !== turnoverSelection.task) {
        setTurnoverSelection((prev) => (prev ? { ...prev, task: fresh } : prev));
      }
      return;
    }
    const created = displayTasks.find((t) => t.reservation_id === turnoverSelection.departingReservationId);
    if (created) {
      setTurnoverSelection((prev) => (prev && !prev.task ? { ...prev, task: created } : prev));
    }
  }, [displayTasks, turnoverSelection?.task?.id, turnoverSelection?.departingReservationId, turnoverSelection?.task]);

  const selectedKey = turnoverSelectionKey(turnoverSelection);

  const monthEmpty =
    monthReservations.filter((r) => r.reservation_type !== "blocked").length === 0;

  const turnoverDetailProps =
    turnoverSelection != null
      ? {
          selection: turnoverSelection,
          reservations: monthReservations,
          propertyId,
          defaultChecklistId: (property as { default_checklist_id?: string | null } | null)?.default_checklist_id ?? null,
          assignOptions: scheduleAssignOptions,
          assigneeLabel: turnoverSelection.task ? getAssigneeLabel(turnoverSelection.task) : "Unassigned",
          isAssigned: turnoverSelection.task ? isAssigned(turnoverSelection.task) : false,
          currentUserId: user?.id ?? null,
          canAssign: canAssignTasks && canAddTasks,
          onClose: () => setTurnoverSelection(null),
        }
      : null;

  const openReservationDrawer = useCallback((reservationPayload: unknown) => {
    setSelectedReservationForDrawer(reservationPayload as Record<string, unknown>);
    setReservationDrawerOpen(true);
  }, []);

  const handleQuickCreateTaskFromDrawer = useCallback(() => {
    if (!selectedReservationForDrawer?.id) return;
    openTurnoverTaskModal({
      mode: "create",
      propertyId,
      reservationId: selectedReservationForDrawer.id as string,
    });
    setReservationDrawerOpen(false);
    setSelectedReservationForDrawer(null);
  }, [selectedReservationForDrawer, openTurnoverTaskModal, propertyId]);

  const reservationHasTask = useMemo(() => {
    if (!selectedReservationForDrawer?.id) return false;
    return displayTasks.some((t) => t.reservation_id === selectedReservationForDrawer.id);
  }, [selectedReservationForDrawer?.id, displayTasks]);

  const handleConfirmRemoveReservation = async () => {
    if (!reservationToRemove) return;
    try {
      await deleteReservation.mutateAsync(reservationToRemove.id);
      toast({
        title: "Removed",
        description: "Reservation removed. It won’t come back on Sync.",
      });
      setReservationToRemove(null);
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : "Failed to remove reservation";
      toast({ title: "Error", description: msg, variant: "destructive" });
    }
  };

  if (liveJobTask) {
    return (
      <LiveJobDesk
        propertyId={propertyId}
        cleaningTaskId={liveJobTask.id}
        cleanerId={liveJobTask.cleaner_id}
        cleanerName={getAssigneeLabel(liveJobTask)}
        mode="live"
        onBack={() => setLiveJobTask(null)}
        onGoToUnit={() => {
          setLiveJobTask(null);
          onGoToUnit?.();
        }}
      />
    );
  }

  return (
    <div className="space-y-5 min-w-0 overflow-hidden" data-tour="tour-schedule-main">
      <SimpleScheduleJobs
        tasks={displayTasks}
        reservations={monthReservations}
        propertyId={propertyId}
        loading={isLoading || tasksLoading}
        syncing={syncing}
        syncFailed={syncFailed}
        lastSyncAt={lastSyncAt}
        canAddTasks={canAddTasks}
        hasIcalUrls={hasIcalUrls}
        isAssigned={isAssigned}
        getAssigneeLabel={getAssigneeLabel}
        onSync={handleSync}
        onConnectIcal={onConnectIcal}
        onOpenJob={(task) => openEditModal(task)}
        onOpenLiveJob={(task) => setLiveJobTask(task)}
        onAssign={(task) => openEditModal(task)}
        onCreateTestClean={() => setTestFlowModalOpen(true)}
        scheduleView={scheduleView}
        onScheduleViewChange={setScheduleViewPersisted}
        monthContent={
          <div className="flex min-w-0 flex-col gap-5 lg:flex-row lg:items-start">
            <div className={cn("min-w-0 flex-1", turnoverSelection && !isMobile && "lg:max-w-[calc(100%-360px)]")}>
              <ScheduleMonthCalendar
                currentDate={calendarDate}
                onCurrentDateChange={setCalendarDate}
                reservations={monthReservations}
                tasks={displayTasks}
                isAssigned={isAssigned}
                getAssigneeLabel={getAssigneeLabel}
                getAssigneeAvatarUrl={getAssigneeAvatarUrl}
                selectedKey={selectedKey}
                onSelectTurnover={setTurnoverSelection}
                hasIcalUrls={hasIcalUrls}
                syncing={syncing}
                onSync={handleSync}
                onConnectIcal={onConnectIcal}
                empty={monthEmpty}
              />
            </div>
            {turnoverDetailProps && !isMobile ? (
              <div className="w-full shrink-0 lg:w-[340px]">
                <ScheduleTurnoverDetail {...turnoverDetailProps} />
              </div>
            ) : null}
          </div>
        }
      />

      {turnoverDetailProps && isMobile ? (
        <Drawer
          open={!!turnoverSelection}
          onOpenChange={(open) => {
            if (!open) setTurnoverSelection(null);
          }}
        >
          <DrawerContent className="max-h-[90vh] rounded-t-[22px] border-[#e6edf1] px-0 pb-6">
            <DrawerTitle className="sr-only">Turnover</DrawerTitle>
            <div className="overflow-y-auto px-[18px] pt-1">
              <ScheduleTurnoverDetail {...turnoverDetailProps} compactDates bare />
            </div>
          </DrawerContent>
        </Drawer>
      ) : null}

      {/* Task Detail Drawer */}
      <TaskDetailDrawer
        open={taskDetailDrawerOpen}
        onOpenChange={setTaskDetailDrawerOpen}
        task={selectedTask}
        cleanerName={selectedTask ? getAssigneeLabel(selectedTask) : null}
        companyName={assignedCompany?.company_name || null}
        propertyId={propertyId}
        onEditTask={(t) => {
          setTaskDetailDrawerOpen(false);
          openEditModal(t as CleaningTaskWithRelations);
        }}
      />

      {/* Message Thread Modal */}
      {messagesTaskId && (
        <MessageThreadModal
          open={!!messagesTaskId}
          onOpenChange={(open) => !open && setMessagesTaskId(null)}
          taskId={messagesTaskId}
          taskTitle="Task Messages"
        />
      )}

      {/* Test Flow Modal */}
      <CreateTestFlowModal
        open={testFlowModalOpen}
        onOpenChange={setTestFlowModalOpen}
        propertyId={propertyId}
        defaultChecklistId={property?.default_checklist_id || null}
      />

      {/* Manual Reservation Modal */}
      <AddManualReservationModal
        open={manualReservationModalOpen}
        onOpenChange={setManualReservationModalOpen}
        propertyId={propertyId}
        defaultChecklistId={property?.default_checklist_id ?? null}
        availableCleaners={availableCleaners}
      />

      {/* Remove reservation confirmation (schedule timeline dropdown) */}
      <AlertDialog open={!!reservationToRemove} onOpenChange={(open) => !open && setReservationToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {reservationToRemove?.hasAssignedTask
                ? "This reservation has a task assigned"
                : "Remove this reservation?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {reservationToRemove?.hasAssignedTask ? (
                <>
                  You are removing this reservation and it has a task that is already assigned. The open turnover will be removed with it, and Sync will not bring the reservation back. Are you sure?
                </>
              ) : (
                <>
                  This removes the reservation and its open turnover from your schedule. Sync will not bring it back.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRemoveReservation}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteReservation.isPending ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reservation Detail Drawer (from calendar click) */}
      {selectedReservationForDrawer?.id && (
        <ReservationDetailDrawer
          open={reservationDrawerOpen}
          onOpenChange={(open) => {
            setReservationDrawerOpen(open);
            if (!open) setSelectedReservationForDrawer(null);
          }}
          reservation={selectedReservationForDrawer as { id: string; start_date: string; end_date: string; source?: string | null; summary?: string | null; check_in_time?: string | null; check_out_time?: string | null; reservation_type?: string }}
          propertyId={propertyId}
          hasTask={reservationHasTask}
          onCreateTask={handleQuickCreateTaskFromDrawer}
          creating={false}
          onRemoved={() => {
            setReservationDrawerOpen(false);
            setSelectedReservationForDrawer(null);
          }}
        />
      )}
    </div>
  );
};

export default ScheduleTab;
